import React, { useCallback, useEffect, useState } from "react";
import {
    getBranches, createBranch, renameBranch, deleteBranch, Branch,
} from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import { useConfirm } from "../common/ConfirmDialog";
import { can } from "../../utils/roles";
import {
    FaPlus, FaEdit, FaTrash, FaSave, FaTimes, FaLock, FaSyncAlt, FaStar,
} from "react-icons/fa";

/**
 * The branch master. Super users and IT only.
 *
 * HEAD OFFICE is seeded protected and default: it cannot be deleted, so there
 * is never a moment with no branches and never a profile pointing at a branch
 * that has gone. It can still be renamed — protection is about deletion, and
 * everything references the id rather than the label.
 */

const errorOf = (e: any, fallback: string) =>
    e?.response?.data?.errors?.[0]?.message ?? e?.response?.data?.error ?? fallback;

const BranchManager: React.FC = () => {
    const { profile } = useAuth();
    const confirm = useConfirm();
    const [branches, setBranches] = useState<Branch[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const [newName, setNewName] = useState("");
    const [creating, setCreating] = useState(false);

    const [editingId, setEditingId] = useState<string | null>(null);
    const [editValue, setEditValue] = useState("");
    const [busy, setBusy] = useState<string | null>(null);

    const allowed = can(profile, "branches");
    const editable = can(profile, "branches", "manage");

    const load = useCallback(async () => {
        if (!allowed) return;
        setLoading(true);
        setError("");
        try {
            setBranches(await getBranches());
        } catch (e: any) {
            setError(errorOf(e, "Could not load the branches."));
        } finally {
            setLoading(false);
        }
    }, [allowed]);

    useEffect(() => { load(); }, [load]);

    const add = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newName.trim()) return;
        setCreating(true);
        setError("");
        try {
            const branch = await createBranch(newName.trim());
            setBranches((prev) => [...prev, branch].sort(
                (a, b) => Number(b.isDefault) - Number(a.isDefault) || a.name.localeCompare(b.name),
            ));
            setNewName("");
        } catch (e: any) {
            setError(errorOf(e, "That branch could not be created."));
        } finally {
            setCreating(false);
        }
    };

    const saveRename = async (officeId: string) => {
        if (!editValue.trim()) return;
        setBusy(officeId);
        setError("");
        try {
            await renameBranch(officeId, editValue.trim());
            setBranches((prev) => prev.map((b) =>
                b.officeId === officeId ? { ...b, name: editValue.trim() } : b));
            setEditingId(null);
        } catch (e: any) {
            setError(errorOf(e, "That branch could not be renamed."));
        } finally {
            setBusy(null);
        }
    };

    const remove = async (branch: Branch) => {
        const ok = await confirm({
            title: "Delete this branch?",
            message: `"${branch.name}" will be removed. This cannot be undone.`,
            confirmLabel: "Delete branch",
            tone: "danger",
        });
        if (!ok) return;
        setBusy(branch.officeId);
        setError("");
        try {
            await deleteBranch(branch.officeId);
            setBranches((prev) => prev.filter((b) => b.officeId !== branch.officeId));
        } catch (e: any) {
            // The server refuses a branch that still has staff or bookings on
            // it, and says how many. Surface that verbatim — it tells the user
            // what to do next.
            setError(errorOf(e, "That branch could not be deleted."));
        } finally {
            setBusy(null);
        }
    };

    if (!allowed) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center px-4">
                <div className="max-w-sm text-center">
                    <FaLock className="mx-auto text-gray-400 mb-3" size={30} />
                    <h1 className="text-lg font-semibold text-gray-900 mb-1">Not available</h1>
                    <p className="text-sm text-gray-500">Only head office can manage branches.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-3xl mx-auto px-4 py-8">
            <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-900">Branches</h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Add and rename the branches staff can be assigned to.
                    </p>
                </div>
                <button
                    onClick={load}
                    disabled={loading}
                    className="flex items-center gap-2 text-sm border border-gray-300 rounded-lg px-3 py-2 hover:bg-gray-50 disabled:opacity-50"
                >
                    <FaSyncAlt className={loading ? "animate-spin" : ""} /> Refresh
                </button>
            </div>

            {editable && (
                <form onSubmit={add} className="flex gap-2 mb-5">
                    <input
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        placeholder="New branch name"
                        maxLength={60}
                        className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
                    />
                    <button
                        type="submit"
                        disabled={creating || !newName.trim()}
                        className="inline-flex items-center gap-2 rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
                    >
                        <FaPlus /> {creating ? "Adding…" : "Add branch"}
                    </button>
                </form>
            )}

            {error && (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {error}
                </div>
            )}

            <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100">
                {loading && <div className="px-4 py-10 text-center text-gray-400">Loading…</div>}

                {!loading && branches.length === 0 && (
                    <div className="px-4 py-10 text-center text-gray-400">No branches yet.</div>
                )}

                {!loading && branches.map((b) => {
                    const isBusy = busy === b.officeId;
                    const isEditing = editingId === b.officeId;

                    return (
                        <div key={b.officeId} className="flex items-center gap-3 px-4 py-3">
                            <div className="flex-1 min-w-0">
                                {isEditing ? (
                                    <div className="flex gap-2">
                                        <input
                                            autoFocus
                                            value={editValue}
                                            maxLength={60}
                                            onChange={(e) => setEditValue(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") saveRename(b.officeId);
                                                if (e.key === "Escape") setEditingId(null);
                                            }}
                                            className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
                                        />
                                        <button
                                            onClick={() => saveRename(b.officeId)}
                                            disabled={isBusy}
                                            className="inline-flex items-center gap-1 rounded bg-gray-900 px-2.5 py-1 text-xs text-white disabled:opacity-50"
                                        >
                                            <FaSave /> Save
                                        </button>
                                        <button
                                            onClick={() => setEditingId(null)}
                                            className="inline-flex items-center gap-1 rounded border border-gray-300 px-2.5 py-1 text-xs"
                                        >
                                            <FaTimes />
                                        </button>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-medium text-gray-900">{b.name}</span>
                                        {b.isDefault && (
                                            <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
                                                <FaStar className="text-[10px]" /> Main branch
                                            </span>
                                        )}
                                        <span className="text-xs text-gray-400">{b.officeId}</span>
                                    </div>
                                )}
                            </div>

                            {!isEditing && (
                                <div className="flex gap-2 shrink-0">
                                    {editable && (
                                        <button
                                            onClick={() => { setEditValue(b.name); setEditingId(b.officeId); }}
                                            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
                                        >
                                            <FaEdit /> Rename
                                        </button>
                                    )}

                                    {b.isProtected ? (
                                        <span
                                            className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs text-gray-400"
                                            title="The main branch cannot be deleted"
                                        >
                                            <FaLock /> Protected
                                        </span>
                                    ) : editable ? (
                                        <button
                                            onClick={() => remove(b)}
                                            disabled={isBusy}
                                            className="inline-flex items-center gap-1.5 rounded-lg border border-red-300 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                                        >
                                            <FaTrash /> Delete
                                        </button>
                                    ) : null}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            <p className="mt-4 text-xs text-gray-500">
                HEAD OFFICE is the main branch. New staff start there, and it cannot be deleted —
                you can still rename it. A branch that still has staff or bookings attached cannot
                be deleted either.
            </p>
        </div>
    );
};

export default BranchManager;
